import { getSiteMediaCount, getSiteUserCount } from '@jwplayer/companion/sites';
import { type GetOutput, jwApi } from '@jwplayer/companion-gateway';
import { useMemo, useState } from 'react';
import { type QueryConfig, useQuery } from 'react-query';

import { SessionStore } from '/global-state';
import { useSession } from '/global-state/session/hooks';

type SessionSite = GetOutput<'dashboard-scope', 'read'>['sites'][number];

export function useCurrentSiteId() {
  return SessionStore.useState((s) => s.currentSiteId);
}

export function useCurrentSiteIdOrThrow() {
  const siteId = SessionStore.useState((s) => s.currentSiteId);
  if (!siteId) {
    throw new Error('Missing siteId');
  }
  return siteId;
}

export function useCurrentSite() {
  return useSite(useCurrentSiteId());
}

export function useSite(
  siteId?: string | { siteId?: string },
  options?: QueryConfig<GetOutput<'sites', 'read'>, Error>,
) {
  const _siteId = typeof siteId === 'object' ? siteId.siteId : siteId;
  if (!_siteId) {
    throw new Error('Unknown siteId!');
  }

  const cacheId = ['site', _siteId];

  return useQuery<GetOutput<'sites', 'read'>, Error>(
    cacheId,
    async ({ signal }) => {
      return (await jwApi('sites', 'read', { routeParams: { siteId: _siteId } }, { signal })).data;
    },
    {
      // Set default options

      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,

      ...options,
    },
  );
}

export function useSiteUserCount(siteId: string) {
  const cacheId = ['site-user-count', siteId];

  // Set default options
  const _options = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };

  return useQuery<number, Error>(
    cacheId,
    async ({ signal }) => {
      return (await getSiteUserCount([siteId], { signal })).content[siteId];
    },
    _options,
  );
}

export function useSiteMediaCount(siteId: string) {
  const cacheId = ['site-media-count', siteId];

  // Set default options
  const _options = {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };

  return useQuery<number, Error>(
    cacheId,
    async ({ signal }) => {
      return (await getSiteMediaCount([siteId], { signal })).content[siteId];
    },
    _options,
  );
}

export function useSiteState<AllSitesOption extends boolean = false>(
  defaultSite: 'CURRENT_SITE' | null = 'CURRENT_SITE',
) {
  const { sites } = useSession();
  const currentSiteId = useCurrentSiteIdOrThrow();
  const currentSite = sites.find((s) => s.site_id === currentSiteId);
  if (!currentSite) {
    throw new Error('Could not find current site');
  }

  // Default to current site
  const [selectedSite, setSelectedSite] = useState<null | SessionSite>(defaultSite === null ? null : currentSite);

  const setSite = useMemo(
    () => (id: null | string) => {
      if (id === null) {
        setSelectedSite(null);
        return;
      }

      const foundSite = sites.find((site) => site.site_id === id);
      if (!foundSite) {
        throw new Error('Site id could not be found!');
      }

      setSelectedSite(foundSite);
    },
    [sites],
  );

  return [selectedSite, setSite] as AllSitesOption extends true
    ? [SessionSite | null, (id: string | null) => void]
    : [SessionSite, (id: string) => void];
}
